import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true,
  timeout: 60000
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Allow callers to opt out of the global logout (e.g. initial auth probe
      // or admin password re-prompts like the Danger Zone wipe). When suppressed,
      // we must NOT clear the token either — otherwise a subsequent retry has no
      // Authorization header and 401s again for the wrong reason.
      const suppress = error.config?._suppressAuthRedirect;
      if (!suppress) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login' && !window.location.hash?.includes('session_id')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  createSession: (sessionId) => apiClient.post('/auth/session', { session_id: sessionId }),
  getMe: (config = {}) => apiClient.get('/auth/me', config),
  updateProfile: (data) => apiClient.put('/users/me', data),
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post('/users/me/picture', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  logout: () => apiClient.post('/auth/logout')
};

export const plansAPI = {
  getPlans: (filters) => apiClient.get('/plans', { params: filters }),
  getPlan: (id) => apiClient.get(`/plans/${id}`),
  createPlan: (data) => apiClient.post('/plans', data),
  updatePlan: (id, data) => apiClient.put(`/plans/${id}`, data),
  deletePlan: (id) => apiClient.delete(`/plans/${id}`),
  getPopular: (limit = 4) => apiClient.get('/plans/popular/top', { params: { limit } }),
  trackView: (id) => apiClient.post(`/plans/${id}/view`)
};

export const testimonialsAPI = {
  getTestimonials: () => apiClient.get('/testimonials'),
  adminGetTestimonials: () => apiClient.get('/admin/testimonials'),
  createTestimonial: (data) => apiClient.post('/testimonials', data),
  updateTestimonial: (id, data) => apiClient.put(`/testimonials/${id}`, data),
  deleteTestimonial: (id) => apiClient.delete(`/testimonials/${id}`)
};

export const cartAPI = {
  getCart: () => apiClient.get('/cart'),
  addToCart: (data) => apiClient.post('/cart/add', data),
  removeFromCart: (planId) => apiClient.delete(`/cart/remove/${planId}`),
  clearCart: () => apiClient.delete('/cart/clear')
};

export const checkoutAPI = {
  initCheckout: () => apiClient.post('/checkout/init')
};

export const ordersAPI = {
  getUserOrders: () => apiClient.get('/orders'),
  getOrder: (id) => apiClient.get(`/orders/${id}`),
  downloadInvoice: (id) => apiClient.get(`/orders/${id}/invoice`, { responseType: 'blob' }),
  manualFulfill: (id) => apiClient.post(`/orders/${id}/fulfill`)
};

export const adminAPI = {
  getAllOrders: () => apiClient.get('/admin/orders'),
  getStats: () => apiClient.get('/admin/stats'),
  getContacts: () => apiClient.get('/admin/contacts'),
  syncContactsToMailchimp: () => apiClient.post('/admin/contacts/sync-mailchimp'),
  exportPlansCSV: () => apiClient.get('/admin/plans/export.csv', { responseType: 'blob' }),
  wipeDatabase: (password) => apiClient.post('/admin/cleanup/wipe', { password }, { _suppressAuthRedirect: true })
};

export const settingsAPI = {
  getSettings: () => apiClient.get('/settings'),
  updateSettings: (data) => apiClient.put('/settings', data)
};

export const favouritesAPI = {
  getFavourites: () => apiClient.get('/favourites'),
  addFavourite: (planId) => apiClient.post('/favourites', { plan_id: planId }),
  removeFavourite: (planId) => apiClient.delete(`/favourites/${planId}`)
};

export const buildingTypesAPI = {
  getBuildingTypes: () => apiClient.get('/building-types'),
  createBuildingType: (data) => apiClient.post('/building-types', data),
  updateBuildingType: (code, data) => apiClient.put(`/building-types/${code}`, data),
  deleteBuildingType: (code) => apiClient.delete(`/building-types/${code}`)
};

export const uploadAPI = {
  uploadFile: (file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      }
    });
  },
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/plans/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (fileId) => `${API_BASE_URL}/files/${fileId}`
};

export default apiClient;
